/**
 * StatusBadge Component — Xyden Leadership Assessment Platform
 * Step 6F Green Phase
 *
 * Design system: §7d — 7 badge variants with semantic colors
 */

import React from 'react';
import type { BadgeVariant } from '../../types';

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant: BadgeVariant;
  showDot?: boolean;
  children: React.ReactNode;
}

const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  draft: 'bg-brand-panel text-brand-steel border-brand-steel/50',
  active: 'bg-orange-100 text-brand-active border-brand-active/30',
  published: 'bg-green-50 text-brand-success border-brand-success/40',
  archived: 'bg-brand-panel text-brand-steel border-brand-steel/50',
  error: 'bg-red-50 text-brand-error border-brand-error/40',
  warning: 'bg-amber-50 text-amber-700 border-brand-warning/50',
  pending: 'bg-gray-100 text-gray-500 border-gray-200',
};

const DOT_CLASSES: Record<BadgeVariant, string> = {
  draft: 'bg-brand-steel',
  active: 'bg-brand-active',
  published: 'bg-brand-success',
  archived: 'bg-brand-steel',
  error: 'bg-brand-error',
  warning: 'bg-brand-warning',
  pending: 'bg-gray-400',
};

export function StatusBadge({ variant, showDot, children, ...props }: StatusBadgeProps) {
  return (
    <span
      data-variant={variant}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${VARIANT_CLASSES[variant] ?? 'bg-gray-100 text-gray-500 border-gray-200'}`}
      {...props}
    >
      {showDot && (
        <span
          aria-hidden="true"
          className={`w-1.5 h-1.5 rounded-full ${DOT_CLASSES[variant] ?? 'bg-gray-400'}`}
        />
      )}
      {children}
    </span>
  );
}

export default StatusBadge;
